import axios, {
  type AxiosError,
  AxiosHeaders,
  type AxiosInstance,
  type AxiosResponse,
  type CreateAxiosDefaults,
  HttpStatusCode,
  type InternalAxiosRequestConfig,
} from 'axios';
import axiosRetry from 'axios-retry';
import { ApiError, type ApiErrorKind } from '@common/api/ApiError';
import ConcurrencyManager from '@common/api/ConcurrencyManager';
import demoRequestInterceptor from '@common/api/demoRequestInterceptor';
import { queryClient } from '@common/api/query';
import { brandConfig } from '@common/brand/brandConfig';
import { tokenExpiredCode } from '@common/utils/device/mobileAppConstants';
import localStorageManager from '@common/utils/storage/localStorageManager';
import { LOCAL_STORAGE_JWT_STORAGE_KEY } from '@common/utils/storage/storageKeys';

declare module 'axios' {
  interface AxiosRequestConfig {
    takeTokenFromResponse?: boolean;
    preventLogout?: boolean;
    /**
     * Lets one request adjust how its failure is named. A missing `kind` keeps the standard one;
     * an explicit `serverMessage: undefined` clears the server's text.
     */
    nameError?: (
      status: number,
      body: unknown,
    ) => { kind?: ApiErrorKind; serverMessage?: string; } | undefined;
  }
}

export interface TokenData {
  token: string;
  tokenValidToUtc: string;
  refreshToken: string;
}

interface Token extends Omit<TokenData, 'tokenValidToUtc' | 'refreshToken'> {
  refreshAt: number;
  refreshing?: boolean;
}

let currentToken: Token | undefined;
export const instanceConfig: CreateAxiosDefaults = {
  timeout: 120 * 1000,
  baseURL: '/api',
  headers: {
    'Cache-Control': 'no-cache',
    Pragma: 'no-cache',
    'X-SC-ClientApp-Name': brandConfig.clientAppHeader,
  },
};

const httpClient = axios.create(instanceConfig);

const MAX_CONCURRENT = 5;
ConcurrencyManager(httpClient, MAX_CONCURRENT);

axiosRetry(httpClient, {
  retries: 2,
  shouldResetTimeout: true,
  retryCondition: (error) => {
    if (error.response?.status == null) {
      return false;
    }
    switch (error.response.status) {
      case HttpStatusCode.ServiceUnavailable:
      case HttpStatusCode.GatewayTimeout:
        return true;
      default:
        return false;
    }
  },
  retryDelay: (retryCount: number, error: AxiosError) =>
    axiosRetry.exponentialDelay(retryCount, error, 100),
});

export const retrieveTokenFromLocalStorage = (): TokenData | undefined => {
  const savedToken = localStorageManager.getItem<TokenData>(LOCAL_STORAGE_JWT_STORAGE_KEY)
    ?? {} as TokenData;
  if (!savedToken?.token || !savedToken?.refreshToken || !savedToken?.tokenValidToUtc) {
    return undefined;
  }
  return savedToken;
};

const waitForTokenRefreshCompletion = (
  config: InternalAxiosRequestConfig,
  resolve: (value: InternalAxiosRequestConfig) => void,
): void => {
  if (!currentToken) {
    return;
  }
  if (currentToken.refreshing) {
    window.setTimeout(() => waitForTokenRefreshCompletion(config, resolve), 200);
  } else {
    const modifiedConfig: InternalAxiosRequestConfig = { ...config };
    modifiedConfig.headers.authorization = httpClient.defaults.headers.common.authorization;
    resolve(modifiedConfig);
  }
};

const applyToken = (tokenData: TokenData, skipLocalStorage = false) => {
  if (!skipLocalStorage) {
    localStorageManager.saveItem(LOCAL_STORAGE_JWT_STORAGE_KEY, tokenData);
  }
  const currentDate = Date.now();
  const refreshDate = new Date(tokenData.tokenValidToUtc).getTime();
  currentToken = {
    token: tokenData.token,
    refreshAt: currentDate + (refreshDate - currentDate) / 2,
  };
  httpClient.defaults.headers.common.authorization = `Bearer ${currentToken.token}`;
};

export async function makeRefreshRequest() {
  try {
    const tokenData = retrieveTokenFromLocalStorage();
    await httpClient.post<void>(
      '/auth/v1/Jwt/refresh',
      {
        refreshToken: tokenData?.refreshToken,
      },
      {
        takeTokenFromResponse: true,
      },
    );
  } catch {
    if (currentToken) {
      currentToken.refreshing = false;
    }
  }
}

(function init() {
  const savedToken = retrieveTokenFromLocalStorage();
  if (savedToken) {
    applyToken(savedToken, true);
  }
})();

const cleanupActions: Array<() => void> = [];

/** Registers an action that logout cleanup runs after the cache is cleared. */
export const addActionForClientCleanup = (action: () => void) => cleanupActions.push(action);

export const clientCleanup = () => {
  localStorageManager.removeItem(LOCAL_STORAGE_JWT_STORAGE_KEY);
  delete httpClient.defaults.headers.common.authorization;
  currentToken = undefined;
  queryClient.clear();
  cleanupActions.forEach((action) => action());
};

const forceLogout = (onLogout: Function) => {
  onLogout();
  clientCleanup();
};

let logoutCallback: Function | undefined;
let isMobileUser: (() => boolean) | undefined;

/*
  Catches HTTP 401 from API and logs user out when request config does not contain
  preventLogout=true. Registered at construction so it runs before naming; until
  setupInterceptors provides onLogout it only rethrows.
*/
httpClient.interceptors.response.use(undefined, (error) => {
  const { response, config } = error;
  if (
    logoutCallback
    && response?.status === HttpStatusCode.Unauthorized
    && config
    && !config.preventLogout
  ) {
    const tokenExpired = currentToken?.token && Date.now() > currentToken.refreshAt;
    if (tokenExpired) {
      // Check if localStorage contains different token
      const tokenData = retrieveTokenFromLocalStorage();
      if (tokenData && tokenData.token !== currentToken!.token) {
        applyToken(tokenData);
        return httpClient.request({
          ...config,
          headers: new AxiosHeaders(config.headers).set(
            'authorization',
            `Bearer ${tokenData.token}`,
          ),
        });
      }
    }
    if (isMobileUser?.()) {
      alert(tokenExpiredCode);
    }
    forceLogout(logoutCallback);
    return;
  }
  throw error;
});

const kindByStatus: ReadonlyMap<number, ApiErrorKind> = new Map([
  [HttpStatusCode.BadRequest, 'invalid-input'],
  [HttpStatusCode.Unauthorized, 'unauthorized'],
  [HttpStatusCode.Forbidden, 'forbidden'],
  [HttpStatusCode.NotFound, 'not-found'],
  [HttpStatusCode.Conflict, 'conflict'],
  [HttpStatusCode.TooManyRequests, 'rate-limited'],
]);

const kindOf = (status: number | undefined): ApiErrorKind => {
  if (status === undefined) {
    return 'no-response';
  }
  // Any 5xx; axios has no constant for the end of the range.
  if (status >= HttpStatusCode.InternalServerError && status < 600) {
    return 'server-error';
  }
  return kindByStatus.get(status) ?? 'other';
};

/**
 * Turns every failed request into an ApiError, after retry and logout have had their say.
 * Cancellations and anything that is not an axios error pass through unchanged.
 */
export const nameErrors = (client: AxiosInstance): void => {
  client.interceptors.response.use(undefined, (error: unknown) => {
    if (axios.isCancel(error) || !axios.isAxiosError(error)) {
      throw error;
    }
    const status = error.response?.status;
    const body: unknown = error.response?.data;
    const standard = {
      kind: kindOf(status),
      serverMessage: typeof body === 'string' && body.length > 0 ? body : undefined,
    };
    const override = status === undefined ? undefined : error.config?.nameError?.(status, body);
    const kind = override?.kind ?? standard.kind;
    const serverMessage = override && 'serverMessage' in override
      ? override.serverMessage
      : standard.serverMessage;
    throw new ApiError(kind, { status, serverMessage, cause: error });
  });
};

nameErrors(httpClient);

export const setupInterceptors = (
  onLogout: Function,
  isDemoUser: () => boolean,
  isMobileUserCheck?: () => boolean,
) => {
  logoutCallback = onLogout;
  isMobileUser = isMobileUserCheck;
  if (APP_TYPE !== 'mystiebel') {
    if (!currentToken?.token && !isMobileUser?.()) {
      forceLogout(onLogout);
    }
  }

  const checkedRequest = (
    requestConfig: InternalAxiosRequestConfig,
  ) => (isDemoUser()
    ? demoRequestInterceptor(requestConfig)
    : requestConfig);

  /*
    Sets up request interceptors before each request to check
    if current token needs refresh.
    Starts refresh token process, all other requests are postponed until
    refresh process is finished (currentToken.refreshing=false)
  */
  if (APP_TYPE === 'mystiebel') {
    httpClient.interceptors.request.use((requestConfig: InternalAxiosRequestConfig) => {
      if (!httpClient.defaults.headers.common.authorization) {
        const savedToken = retrieveTokenFromLocalStorage();
        if (savedToken) {
          applyToken(savedToken, true);
          const modifiedConfig: InternalAxiosRequestConfig = { ...requestConfig };
          modifiedConfig.headers.authorization = httpClient.defaults.headers.common.authorization;
          return modifiedConfig;
        } else {
          forceLogout(onLogout);
          return Promise.reject(new Error('Session expired'));
        }
      }
      return requestConfig;
    });
  }
  if (APP_TYPE === 'sw' || APP_TYPE === 'um') {
    httpClient.interceptors.request.use((requestConfig: InternalAxiosRequestConfig) => {
      if (!currentToken || requestConfig.takeTokenFromResponse || isMobileUser?.()) {
        return checkedRequest(requestConfig);
      }
      const { refreshAt, refreshing } = currentToken;
      if (refreshAt > Date.now()) {
        return checkedRequest(requestConfig);
      }
      if (!refreshing) {
        currentToken.refreshing = true;
        makeRefreshRequest();
      }
      return new Promise((resolve) => {
        waitForTokenRefreshCompletion(requestConfig, resolve);
      });
    });

    /*
    Sets up response interceptors to catch new token from response
    when request config contains takeTokenFromResponse=true
  */
    httpClient.interceptors.response.use(
      // The 401 handler before this one can resolve with undefined after a logout; pass that on.
      (response: AxiosResponse<TokenData> | undefined) => {
        if (
          response?.config.takeTokenFromResponse
          && response.data.token
          && response.data.tokenValidToUtc
        ) {
          applyToken({
            token: response.data.token,
            tokenValidToUtc: response.data.tokenValidToUtc,
            refreshToken: response.data.refreshToken,
          });
        }
        return response as AxiosResponse<TokenData>;
      },
    );
  }
};

export default httpClient;
